import React, { useState, useEffect } from "react";
import { CircularProgress } from "@mui/material";
import { useNavigate } from "react-router-dom";
import AnswerPreviewModal from "./AnswerPreviewModal";
import { PRODUCT_TYPES } from "../../../../shared/enums/productTypeList.js";
import Swal from "sweetalert2";
import GlobalButton from "../../../../shared/utils/GlobalButton.jsx";

const AssignQuestionnaireSelection = ({ onClose, selectedCampaignId }) => {
  const [template, setTemplate] = useState([]);
  const [searchProduct, setSearchProduct] = useState("");
  const [selectedProduct, setSelectedProduct] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchProductTemplate, setSearchProductTemplate] = useState("");
  const navigate = useNavigate();
  const serverURL = process.env.REACT_APP_URL;

  const fetchQuestions = async (searchTerm = "") => {
    try {
      const url = `${serverURL}/PDI/fetchQuestionnairesLinkedWithCampaign?search=${encodeURIComponent(
        searchTerm,
      )}&campaignId=${encodeURIComponent(selectedCampaignId)}`;

      const res = await fetch(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (!res.ok) throw new Error(`HTTP error! Status: ${res.status}`);

      const data = await res.json();
      return data;
    } catch (err) {
      console.error("Error fetching questions:", err);
      return [];
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        let data = await fetchQuestions();
        setTemplate(data.data || []);
      } catch (error) {
        console.error("Error loading data:", error);
        setTemplate([]);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const filteredProducts = PRODUCT_TYPES.filter((product) =>
    product.toLowerCase().includes(searchProduct.toLowerCase()),
  );

  const handleProductChange = (product) => {
    setSelectedProduct((prev) =>
      prev.includes(product)
        ? prev.filter((c) => c !== product)
        : [...prev, product],
    );
  };

  const filteredTemplates = template.filter((t) => {
    const matchesProduct =
      selectedProduct.length === 0 || selectedProduct.includes(t.productType);
    const matchesSearch = t?.productType
      ?.toLowerCase()
      .includes(searchProductTemplate.toLowerCase());
    return matchesProduct && matchesSearch;
  });

  const handleDeleteTemplate = async (templateId) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You want to delete this template?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await fetch(
            `${serverURL}/PDI/deleteQuestionnaireLinkedWithCampaign/${templateId}`,
            {
              method: "DELETE",
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            },
          );

          const data = await res.json();

          if (res.ok) {
            // 🔥 Remove from local state

            Swal.fire({
              icon: "success",
              title: "Deleted!",
              text: data.message || "Template deleted successfully.",
              timer: 1500,
              showConfirmButton: false,
            });
            console.log(data);
            let questionData = await fetchQuestions();
            setTemplate(questionData.data || []);
          } else {
            Swal.fire({
              icon: "error",
              title: "Failed!",
              text: data.message || "Something went wrong while deleting.",
            });
          }
        } catch (error) {
          Swal.fire({
            icon: "error",
            title: "Error!",
            text: "Server not reachable or unknown error.",
          });
          console.error("Delete error:", error);
        }
      }
    });
  };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
      <div className="bg-gray-50 rounded-3xl p-6 w-[95%] max-w-8xl shadow-xl relative overflow-hidden max-h-[90vh] flex flex-col">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-4 text-gray-500 hover:text-gray-700 text-2xl"
        >
          &times;
        </button>

        {/* Header */}
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-bold mb-4 pl-2">
            Campaign Question Library
          </h2>
        </div>

        {/* Content */}
        <div className="flex flex-1 overflow-hidden gap-6">
          {/* Sidebar */}
          <div className="w-1/5 bg-white border rounded-xl p-4 shadow-sm flex-shrink-0 h-full overflow-y-auto">
            <h3 className="text-sm font-semibold mb-3 text-gray-700">
              Products
            </h3>
            <input
              type="text"
              placeholder="Search Products..."
              value={searchProduct}
              onChange={(e) => setSearchProduct(e.target.value)}
              className="w-full mb-3 px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-400"
            />
            <ul className="space-y-2 text-sm max-h-[400px] overflow-y-auto text-gray-700">
              {filteredProducts.length === 0 ? (
                <li className="text-gray-400 text-xs">No product found</li>
              ) : (
                filteredProducts.map((temp, index) => (
                  <li key={index}>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        className="accent-indigo-500"
                        checked={selectedProduct.includes(temp)}
                        onChange={() => handleProductChange(temp)}
                      />
                      {temp}
                    </label>
                  </li>
                ))
              )}
            </ul>
          </div>

          {/* Right Section */}
          <div className="flex-1 overflow-y-auto pr-2">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <CircularProgress />
              </div>
            ) : filteredTemplates.length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <div className="flex flex-col gap-2 items-center">
                  <p className="text-gray-500 font-semibold text-2xl">
                    No templates found
                  </p>
                  <GlobalButton
                    variant="primary"
                    size="medium"
                    onClick={() => navigate("/adminportal/pdieditabletable")}
                  >
                    {`Create Template`}
                  </GlobalButton>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-6 pb-4">
                {filteredTemplates.map((temp) => (
                  <div
                    key={temp._id}
                    className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm hover:shadow-md transition-all duration-300 flex flex-col justify-between"
                  >
                    {/* Header */}
                    <div className="flex justify-between items-center mb-3">
                      <h3 className="text-lg font-semibold text-gray-800">
                        {temp.productType}
                      </h3>
                      <span className="text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded-full w-[100px] text-center">
                        {temp.questions.length} Question
                        {temp.questions.length > 1 ? "s" : ""}
                      </span>
                    </div>

                    {/* Question Preview */}
                    <div className="border-t pt-2 space-y-2 max-h-40 overflow-y-auto">
                      {temp.questions.map((q, i) => (
                        <div
                          key={i}
                          className="text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-lg p-2"
                        >
                          <p className="font-medium text-gray-900">
                            {i + 1}. {q.question}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            <span className="font-semibold">Category:</span>{" "}
                            {q.category} |{" "}
                            <span className="font-semibold">Type:</span>{" "}
                            {q.questionType}
                          </p>
                        </div>
                      ))}
                    </div>

                    {/* Show Answers Button */}
                    <div className="flex justify-between mt-3 gap-2">
                      <button
                        onClick={() => setSelectedTemplate(temp)}
                        className="flex-1 bg-[#418cff] text-white py-2 rounded-md text-sm font-semibold hover:bg-[#2f6be0] transition"
                      >
                        Show Answers
                      </button>
                      <button
                        onClick={() => handleDeleteTemplate(temp._id)} // 🔥 NEW
                        className="flex-1 bg-red-500 text-white py-2 rounded-md text-sm font-semibold hover:bg-red-600 transition"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Modal */}
        {selectedTemplate && (
          <AnswerPreviewModal
            template={selectedTemplate}
            onClose={() => setSelectedTemplate(null)}
          />
        )}
      </div>
    </div>
  );
};

export default AssignQuestionnaireSelection;
